import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AccessTime,
} from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiClient } from '../../../../api/httpClient';
import Navbar from '../../../../globals/components/Navbar';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

const Announcement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Form state
  const [formData, setFormData] = useState({
    heading: '',
    description: '',
    type: 'general',
  });

  const [formErrors, setFormErrors] = useState({});

  // Announcement types
  const announcementTypes = [
    { value: 'general', label: 'General', color: 'default' },
    { value: 'important', label: 'Important', color: 'warning' },
    { value: 'urgent', label: 'Urgent', color: 'error' },
    { value: 'maintenance', label: 'Maintenance', color: 'info' },
    { value: 'update', label: 'Update', color: 'success' },
  ];

  // Fetch all announcements
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await apiClient('/announcements/', 'GET');
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();
  const userdetails = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (userdetails?.role?.toLowerCase() === 'internal_user') {
      const fetchPermissions = async () => {
        try {
          const roleId = userdetails.role_id;
          if (roleId) {
            const response = await apiClient(`/get-internal-user/${roleId}`, "GET");
            const features = response.features || [];
            if (!features.includes('announcements')) {
              toast.error("You do not have permission to view this page.");
              navigate('/');
            }
          }
        } catch (error) {
          console.error("Error checking permissions:", error);
        }
      };
      fetchPermissions();
    }
  }, [userdetails, navigate]);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.heading.trim()) {
      errors.heading = 'Heading is required';
    } else if (formData.heading.length > 200) {
      errors.heading = 'Heading must be less than 200 characters';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    if (!formData.type) {
      errors.type = 'Type is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Open dialog for create
  const handleOpenCreate = () => {
    setEditMode(false);
    setCurrentAnnouncement(null);
    setFormData({
      heading: '',
      description: '',
      type: 'general',
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  // Open dialog for edit
  const handleOpenEdit = (announcement) => {
    setEditMode(true);
    setCurrentAnnouncement(announcement);
    setFormData({
      heading: announcement.heading || '',
      description: announcement.description || '',
      type: announcement.type || 'general',
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentAnnouncement(null);
    setFormData({
      heading: '',
      description: '',
      type: 'general',
    });
    setFormErrors({});
  };

  // Create announcement
  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      await apiClient('/announcements/', 'POST', formData);
      toast.success('Announcement created successfully');
      handleCloseDialog();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error creating announcement:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      toast.error('Failed to create announcement');
    }
  };

  // Update announcement
  const handleUpdate = async () => {
    if (!validateForm()) return;

    if (!currentAnnouncement?.announcement_id) return;

    try {
      await apiClient(
        `/announcements/${currentAnnouncement.announcement_id}/`,
        'PUT',
        formData
      );
      toast.success('Announcement updated successfully');
      handleCloseDialog();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error updating announcement:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      toast.error('Failed to update announcement');
    }
  };

  // Open delete confirmation
  const handleOpenDeleteConfirm = (announcement) => {
    setAnnouncementToDelete(announcement);
    setDeleteConfirmOpen(true);
  };

  // Close delete confirmation
  const handleCloseDeleteConfirm = () => {
    setAnnouncementToDelete(null);
    setDeleteConfirmOpen(false);
  };

  // Delete announcement
  const handleDelete = async () => {
    if (!announcementToDelete) return;

    try {
      await apiClient(
        `/announcements/${announcementToDelete.announcement_id}/`,
        'DELETE'
      );
      toast.success('Announcement deleted successfully');
      handleCloseDeleteConfirm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  // Get chip color for type
  const getTypeColor = (type) => {
    const typeObj = announcementTypes.find((t) => t.value === type);
    return typeObj?.color || 'default';
  };

  // Get type label
  const getTypeLabel = (type) => {
    const typeObj = announcementTypes.find((t) => t.value === type);
    return typeObj?.label || type;
  };

  // Filter announcements
  const filteredAnnouncements = announcements.filter((announcement) => {
    const matchesSearch =
      announcement.heading?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || announcement.type === filterType;
    return matchesSearch && matchesType;
  });

  // Pagination helpers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const currentRows = filteredAnnouncements.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Navbar />
      <ToastContainer />
      <Box sx={{ maxWidth: '100%', padding: '20px' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
          Announcements
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search announcements..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: '300px',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
              slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={filterType}
                label="Filter by Type"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="all">All Types</MenuItem>
                {announcementTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add New Announcement
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ width: '100%', overflowX: 'auto', mb: 2, borderRadius: '8px' }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: '#f0f0f0' }}>
                  <TableCell>Type</TableCell>
                  <TableCell>Heading</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Created</TableCell>
                  <TableCell align="right">Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((announcement) => (
                  <TableRow key={announcement.announcement_id}>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(announcement.type)}
                        color={getTypeColor(announcement.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{announcement.heading || 'N/A'}</TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {announcement.description || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
                        <AccessTime sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="body2">
                          {formatRelativeTime(announcement.created_at)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatRelativeTime(announcement.updated_at)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleOpenEdit(announcement)}
                        color="secondary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDeleteConfirm(announcement)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredAnnouncements.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {editMode ? 'Edit Announcement' : 'Add New Announcement'}
        </DialogTitle>
        <DialogContent sx={{ width: '500px' }}>
          <TextField
            autoFocus
            margin="dense"
            label="Heading"
            name="heading"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.heading}
            onChange={handleInputChange}
            error={!!formErrors.heading}
            helperText={formErrors.heading || `${formData.heading.length}/200 characters`}
            slotProps={{ htmlInput: { maxLength: 200 }}}
          />
          <TextField
            margin="dense"
            label="Description"
            name="description"
            fullWidth
            variant="outlined"
            value={formData.description}
            onChange={handleInputChange}
            error={!!formErrors.description}
            helperText={formErrors.description}
            multiline
            rows={4}
          />
          <FormControl fullWidth margin="dense" error={!!formErrors.type}>
            <InputLabel>Type</InputLabel>
            <Select
              name="type"
              value={formData.type}
              label="Type"
              onChange={handleInputChange}
            >
              {announcementTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {formErrors.type && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {formErrors.type}
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={editMode ? handleUpdate : handleCreate}
            color="primary"
            variant="contained"
          >
            {editMode ? 'Save Changes' : 'Add Announcement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent sx={{ width: '400px' }}>
          <Typography>
            Are you sure you want to delete this announcement?
          </Typography>
          {announcementToDelete && (
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                {announcementToDelete.heading}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {announcementToDelete.description}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Announcement;

